import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { USER_PERMISSIONS } from "@/roles/types/enums";

import {
  CheckCircleIcon,
  ChevronRightIcon,
  colors,
  RadioButtonUncheckedIcon
} from "@dotnet/telematics-design-system";

import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import { ROUTES } from "../../../../../../shared";
import ListChoice from "../../../../../ListChoice/ListChoice";
import useTutorialStartActions from "../../../../../TutorialMode/hooks/useTutorialStartActions";
import {
  STEP_GROUP,
  TUTORIAL_NAMES_FROM_USERS
} from "../../../../../TutorialMode/types";
import { SETTINGS_CONTENT_NAMES } from "../../../../types/enums";
import SettingsControlsContentWrapper from "./SettingsControlsContentWrapper";

interface TutorialProps {
  isMobile: boolean;
  closeMenu: () => void;
  setSettingsContent: (newContent: SETTINGS_CONTENT_NAMES) => void;
}

const Tutorials = ({
  isMobile,
  closeMenu,
  setSettingsContent
}: TutorialProps) => {
  const { textDisabled, green600 } = colors;
  const { user, getAccessStatus } = useAuth();

  const location = useLocation();
  const { t } = useTranslation();

  const {
    startApplyScheduleTutorial,
    startCompanyCardTutorial,
    startCreateScheduleTutorial,
    startCompanySelectorTutorial
  } = useTutorialStartActions(
    isMobile,
    location.pathname as ROUTES,
    setSettingsContent,
    closeMenu
  );

  const startTutorial = (stepGroup: STEP_GROUP) => {
    switch (stepGroup) {
      case STEP_GROUP.TutorialCompanySelector:
        startCompanySelectorTutorial();
        break;

      case STEP_GROUP.TutorialNewCompanyCard:
        startCompanyCardTutorial();
        break;

      case STEP_GROUP.TutorialCreateSchedule:
        startCreateScheduleTutorial();
        break;

      case STEP_GROUP.TutorialApplySchedule:
        startApplyScheduleTutorial();
        break;

      default:
        closeMenu();
        break;
    }
  };

  const checkIfTutorialCompleted = (tutorialName: STEP_GROUP) => {
    const convertedNames: { [key: string]: TUTORIAL_NAMES_FROM_USERS } = {
      [STEP_GROUP.TutorialApplySchedule]:
        TUTORIAL_NAMES_FROM_USERS.ApplySchedule,
      [STEP_GROUP.TutorialCreateSchedule]:
        TUTORIAL_NAMES_FROM_USERS.CreateSchedule,
      [STEP_GROUP.TutorialNewCompanyCard]:
        TUTORIAL_NAMES_FROM_USERS.CompanyCards,
      [STEP_GROUP.TutorialCompanySelector]:
        TUTORIAL_NAMES_FROM_USERS.companySelector
    };

    return user?.tutorialStatus[convertedNames[tutorialName]] ? (
      <CheckCircleIcon color={green600} />
    ) : (
      <RadioButtonUncheckedIcon style={{ color: textDisabled }} />
    );
  };

  const canSeeScheduleTutorials = getAccessStatus(
    USER_PERMISSIONS.ManageSchedules
  );

  return (
    <SettingsControlsContentWrapper onGoBackButton={setSettingsContent}>
      <ListChoice
        title={t("Tutorials##company selector##name")}
        iconLeft={checkIfTutorialCompleted(STEP_GROUP.TutorialCompanySelector)}
        iconRight={<ChevronRightIcon />}
        onClick={() => startTutorial(STEP_GROUP.TutorialCompanySelector)}
      />
      <ListChoice
        title={t("Tutorials##add company card##name")}
        iconLeft={checkIfTutorialCompleted(STEP_GROUP.TutorialNewCompanyCard)}
        iconRight={<ChevronRightIcon />}
        onClick={() => startTutorial(STEP_GROUP.TutorialNewCompanyCard)}
      />

      {canSeeScheduleTutorials && (
        <>
          <ListChoice
            title={t("Tutorials##create schedule##name")}
            iconLeft={checkIfTutorialCompleted(
              STEP_GROUP.TutorialCreateSchedule
            )}
            iconRight={<ChevronRightIcon />}
            onClick={() => startTutorial(STEP_GROUP.TutorialCreateSchedule)}
          />
          <ListChoice
            title={t("Tutorials##apply schedule##name")}
            iconLeft={checkIfTutorialCompleted(
              STEP_GROUP.TutorialApplySchedule
            )}
            iconRight={<ChevronRightIcon />}
            onClick={() => startTutorial(STEP_GROUP.TutorialApplySchedule)}
          />
        </>
      )}
    </SettingsControlsContentWrapper>
  );
};

export default Tutorials;
